import React, { useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import { ClusterWithCr } from "../../solverHooks/useConversationDD";
import { PatientColorMap } from "../../layout/patientColorMap";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useTranslate } from "../../hooks/useTranslate";
import styled from "@mui/material/styles/styled";
import { Theme } from "@mui/material";
import { useWidgetSettings } from "../../widget/useWidgetSettings";
import { getPartnerUrl } from "fe-common/utils/getPartnerUrl";
import { symptomTxt } from "./SymptomText";
import { StrengthLinear } from "./strengthLinear";
import { ReactComponent as RareDiseaseIcon } from "../../assets/rareDiseaseIcon.svg";
import { DdFlagType } from "fe-common/graphql";
import { MoreInfoButton } from "./moreInfoButton";
import { INFOMED_CLINIC_ID, INFOMED_PARTNER_ID } from "../../widget/partnerIds";
import { useHistory } from "react-router";
import { PatientRoute } from "../../PatientRoutes/PatientRoute";
import { MoreAboutDiseaseButton } from "./moreAboutDiseaseButton";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
import { useClinicData } from "../../clinic/clinicHooks";
import { URGENCY_DATA } from "fe-web-components/data/urgencyData";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ShowInChatReasoningButton } from "../demodebug/mobileSuggestionZoom/showInChatReasoningButton";
import { marginRightToMarginLeftOnRtl } from "fe-web-components/rtl/rtlStyles";

//region [[ Styles ]]
const DiseaseCardContainer = styled(Card)(
  ({ theme, linkable }: { theme?: Theme; linkable: "true" | "false" }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: "22px 0",
    boxShadow: "0px 0px 4px rgb(0 0 0 / 30%)",
    borderRadius: "8px",
    lineHeight: "20px",
    cursor: linkable === "true" ? "pointer" : "",
    "&:hover": {
      opacity: linkable === "true" ? "0.8" : "1",
      background: linkable === "true" ? PatientColorMap.Background : "white",
    },
  }),
);

const Title = styled("div")({
  fontSize: "20px",
  backgroundColor: PatientColorMap.Primary,
  color: "white",
  fontWeight: "bold",
  lineHeight: "25px",
  padding: "15px",
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-between",
  alignItems: "center",
});

const AdviceWrapper = styled("div")({
  margin: "10px 0",
  display: "none",
});

const Advice = styled("div")(
  ({ theme, color }: { theme?: Theme; color: KahunColorMap }) => ({
    fontWeight: "bold",
    fontSize: "14px",
    color: color,
  }),
);

const TopContainer = styled("div")({
  padding: "15px",
  paddingTop: "0px",
});

const StrengthWrapper = styled("div")({
  marginTop: "10px",

  marginBottom: "10px",
});

const StrengthArea = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const MoreInfoArea = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
});

const PrimaryDiseaseDisclaimer = styled("div")({
  backgroundColor: PatientColorMap.PrimaryLight,
  color: PatientColorMap.Primary,
  fontSize: "14px",
  fontWeight: 600,
  padding: "15px",
  lineHeight: "14px",
});

const RareDisease = styled("div")({
  color: "#243443",
  height: "fit-content",
  fontSize: "12px",
  marginTop: "15px",
  display: "flex",
  marginBottom: "10px",
  alignItems: "center",
  fontWeight: 500,
  flex: 1,
});

const RareDiseaseIconStyled = styled(RareDiseaseIcon)({
  ...marginRightToMarginLeftOnRtl(5),
  "& path[fill]": {
    fill: PatientColorMap.Primary,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]
export interface DiseaseViewProps {
  className?: string;
  cluster: ClusterWithCr;
  getFindingsStrength: any;
}
//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

const DiseaseView = ({ cluster, ...props }: DiseaseViewProps) => {
  const urgencyLevel = cluster.urgencyId;

  const history = useHistory();

  const { partnerId, utmString } = useWidgetSettings();

  const showCR = usePatientFeatureFlags(
    "showClinicalReasoningOnP1st",
  ) as boolean;

  const { clinicSettings, clinic: clinicId } = useClinicData();

  const linkToExternalDisease = useMemo(
    () => clinicSettings.linkToExternalDisease,
    [clinicSettings],
  );
  const { translate } = useTranslate();

  const isRare = cluster.flags.find((flag) => flag === DdFlagType.Rare);

  const diseaseTexts = useMemo(
    () => symptomTxt(cluster.title).toString() || cluster.title?.name,
    [cluster],
  );

  const goToDisease = useCallback(
    (e) => {
      // Put a flag for clicking on the disease so the widget will know
      // to make it's height smaller
      sessionStorage.setItem("diseaseClicked", cluster.title.id);
      // Navigate to the disease url
      window.location.href = getPartnerUrl(
        partnerId || clinicId,
        cluster.title.id,
        window.location.host,
        utmString,
      );
    },
    [partnerId, clinicId, cluster, utmString],
  );

  const handleDiseaseClicked = useCallback(
    (e) => {
      e.stopPropagation();

      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_CLICKED_DISEASE,
        label: `${diseaseTexts} - ${urgencyLevel}`,
        partnerId:
          partnerId || clinicId === INFOMED_CLINIC_ID
            ? INFOMED_PARTNER_ID
            : clinicId,
      });
      // console.log({ cluster, partnerId });

      if (linkToExternalDisease) {
        goToDisease(e);
      }
    },
    [
      diseaseTexts,
      urgencyLevel,
      partnerId,
      clinicId,
      goToDisease,
      linkToExternalDisease,
    ],
  );

  const moreInfoClicked = useCallback(
    (e) => {
      e.stopPropagation();

      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_CLICKED_MORE_INFO,
        label: `${diseaseTexts} - ${urgencyLevel}`,
        partnerId,
      });
      // console.log({ cluster, partnerId });
      history.push("/" + PatientRoute.MoreInfo + "/" + cluster.id);
    },
    [diseaseTexts, urgencyLevel, partnerId, cluster.id, history],
  );

  const clinicalReasoningExist = props.getFindingsStrength && showCR;

  return (
    <DiseaseCardContainer
      className={props.className}
      onClick={handleDiseaseClicked}
      linkable={"false"}
    >
      <Title>
        {diseaseTexts}
        <ShowInChatReasoningButton cluster={cluster} />
      </Title>
      {cluster.isPrimaryDisease && (
        <PrimaryDiseaseDisclaimer>
          {translate("EXPLAINED_BY_CHRONIC_DISORDER")}
        </PrimaryDiseaseDisclaimer>
      )}
      <TopContainer>
        <StrengthArea>
          <StrengthWrapper>
            <StrengthLinear prob={cluster.prob * 100} />
          </StrengthWrapper>
        </StrengthArea>

        <MoreInfoArea>
          {isRare && (
            <RareDisease>
              <RareDiseaseIconStyled />
              {translate("RARE_DISEASE")}
            </RareDisease>
          )}
          <AdviceWrapper>
            <Advice color={URGENCY_DATA[urgencyLevel].color}>
              {URGENCY_DATA[urgencyLevel].generalText}
            </Advice>
          </AdviceWrapper>
          {linkToExternalDisease && (
            <MoreAboutDiseaseButton onClick={handleDiseaseClicked} />
          )}
          {!linkToExternalDisease && clinicalReasoningExist && (
            <MoreInfoButton onClick={moreInfoClicked} />
          )}
        </MoreInfoArea>
      </TopContainer>
    </DiseaseCardContainer>
  );
};

export default DiseaseView;
